if (!customElements.get("video-reels")) {
  customElements.define(
    "video-reels",
    class VideoReels extends HTMLElement {
      constructor() {
        super();
        this.activeClass = "active";
        this.playingClass = "is-playing";
        this.swiper = this.querySelector(".wt-slider__container");
      }

      connectedCallback() {
        this._sectionObserver = null;
        this.init();
      }

      disconnectedCallback() {
        if (this._sectionObserver) {
          this._sectionObserver.disconnect();
          this._sectionObserver = null;
        }
        this.removeEventListener('theme:swiper-ready', this.onSwiperReady);
        this.boundSwiper = null;
        this.pauseAllSlides();
      }

      observeSection() {
        // Pause any playing video when the section scrolls out of view.
        const sectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                this.pauseAllSlides();
              }
            });
          },
          { root: null, rootMargin: "0px", threshold: 0.1 },
        );
        sectionObserver.observe(this);
        this._sectionObserver = sectionObserver;
      }

      pauseAllSlides() {
        this.querySelectorAll("[data-swiper-slide]").forEach((slide) => {
          const v = slide.querySelector("video");
          if (v && !v.paused) v.pause();
          slide.classList.remove(this.playingClass);
        });
      }

      sanitizeVideosOnce() {
        if (this._sanitized) return;
        this._sanitized = true;
        this.querySelectorAll("video").forEach((video) => {
          video.pause();
          video.muted = true;
          video.removeAttribute("autoplay");
          video.autoplay = false;
          video.setAttribute("playsinline", "");
          video.setAttribute("webkit-playsinline", "");
          video.preload = "metadata";
        });
      }

      findActiveSlide(swiper) {
        return swiper.slides[swiper.activeIndex];
      }

      toggleActiveClass(swiper) {
        const activeSlide = this.findActiveSlide(swiper);
        swiper.slides.forEach((slide) => {
          slide.classList.toggle(this.activeClass, slide === activeSlide);
        });
      }

      handleSlideChange(swiper) {
        this.toggleActiveClass(swiper);
        this.pauseAllSlides();
      }

      bindPlayToggles(swiper) {
        swiper.slides.forEach((slide) => {
          if (slide._reelBound) return;
          slide._reelBound = true;
          const video = slide.querySelector("video");
          const trigger = slide.querySelector("[data-reel-toggle]");
          if (!video) return;

          // Reflect playback state on the slide so CSS can hide/show overlays.
          video.addEventListener("play", () => {
            slide.classList.add(this.playingClass);
          });
          video.addEventListener("pause", () => {
            slide.classList.remove(this.playingClass);
          });
          video.addEventListener("ended", () => {
            slide.classList.remove(this.playingClass);
          });

          const toggle = (e) => {
            // Inactive slide: let swiper navigate first; user can re-tap to play.
            if (!slide.classList.contains(this.activeClass)) return;
            e.preventDefault();
            e.stopPropagation();
            if (video.paused) {
              // Pause every other slide before playing this one.
              swiper.slides.forEach((s) => {
                if (s !== slide) {
                  const v = s.querySelector("video");
                  if (v && !v.paused) v.pause();
                  s.classList.remove(this.playingClass);
                }
              });
              // User-initiated play → enable sound. Videos start muted only to
              // satisfy pre-interaction autoplay policies; after an explicit
              // tap, browsers permit audio — a silent testimonial says nothing.
              video.muted = false;
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          };

          // Tapping anywhere on the active slide (video, overlays, play button)
          // toggles playback. The trigger button stops propagation so we don't
          // double-fire.
          if (trigger) trigger.addEventListener("click", toggle);
          slide.addEventListener("click", toggle);
        });
      }

      addVideoEventHandlers(swiper) {
        this._onSlideChange = () => this.handleSlideChange(swiper);
        swiper.on("slideChange", this._onSlideChange);
        swiper.on("slidesLengthChange", () => this.bindPlayToggles(swiper));
        swiper.on("update", () => this.bindPlayToggles(swiper));
      }

      checkSwiperInitialization() {
        const swiperContainer = this.swiper;
        const mySwiperInstance = swiperContainer?.swiper;

        if (
          swiperContainer?.classList.contains("swiper-initialized") &&
          mySwiperInstance && this.boundSwiper !== mySwiperInstance
        ) {
          this.boundSwiper = mySwiperInstance;
          this.sanitizeVideosOnce();
          this.addVideoEventHandlers(mySwiperInstance);
          this.toggleActiveClass(mySwiperInstance);
          this.bindPlayToggles(mySwiperInstance);
          this._sectionObserver?.disconnect();
          this.observeSection();
        }
      }

      init() {
        this.sanitizeVideosOnce();
        this.onSwiperReady = this.checkSwiperInitialization.bind(this);
        this.addEventListener('theme:swiper-ready', this.onSwiperReady);
        this.checkSwiperInitialization();
      }
    },
  );
}
